import { useEffect, useState } from "react";

import {
  ErrorApi,
  borrarAsset,
  listarAssets,
  quitarFondoAsset,
  subirAsset,
} from "../api/client";
import type { Asset, TipoAsset } from "../api/types";
import estilos from "./PanelAssets.module.css";

interface Props {
  token: string;
  proyectoId: string;
  // El padre (SeccionMarca) necesita saber la lista actual para resolver la
  // miniatura del logo en PanelBrandKit, y si conviene refrescar el kit de
  // marca (subida/borrado de un asset tipo "logo" lo cambia server-side).
  onCambio: (assets: Asset[], posibleCambioLogo: boolean) => void;
}

const ETIQUETA_TIPO: Record<TipoAsset, string> = {
  logo: "Logo",
  foto_producto: "Foto de producto",
  textura_fondo: "Textura de fondo",
  otro: "Otro",
};

const ACEPTA_ARCHIVOS = "image/png,image/jpeg,image/webp";

function mensajeDeError(err: unknown, generico: string): string {
  if (err instanceof ErrorApi) return err.message;
  if (err instanceof Error) return err.message;
  return generico;
}

function formatearTamano(bytes: number | null): string {
  if (bytes === null) return "";
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
}

// Subida/listado/borrado de assets del proyecto (logo, fotos, texturas) +
// boton "Quitar fondo" sobre una foto de producto (M5b). Autosuficiente:
// hace su propio GET/POST/DELETE, igual que PanelVersiones.tsx.
export function PanelAssets({
  token,
  proyectoId,
  onCambio,
}: Props): JSX.Element {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  const [subiendo, setSubiendo] = useState(false);
  const [erroresSubida, setErroresSubida] = useState<string[]>([]);
  const [arrastrando, setArrastrando] = useState(false);

  const [borrandoId, setBorrandoId] = useState<string | null>(null);
  const [errorBorrado, setErrorBorrado] = useState<string | null>(null);

  const [quitandoFondoId, setQuitandoFondoId] = useState<string | null>(null);
  const [resultadosSinFondo, setResultadosSinFondo] = useState<
    Record<string, Asset>
  >({});
  const [erroresSinFondo, setErroresSinFondo] = useState<
    Record<string, string>
  >({});

  const cargar = async (): Promise<void> => {
    try {
      const datos = await listarAssets(token, proyectoId);
      setAssets(datos);
      setErrorCarga(null);
      onCambio(datos, false);
    } catch (err) {
      setErrorCarga(mensajeDeError(err, "No se pudieron cargar los assets"));
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    setCargando(true);
    void cargar();
    // Solo al montar / cambiar de proyecto: subir/borrar refrescan a mano.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, proyectoId]);

  const subirArchivos = async (archivos: File[]): Promise<void> => {
    if (archivos.length === 0 || subiendo) return;
    setSubiendo(true);
    setErroresSubida([]);
    const fallos: string[] = [];
    let huboLogo = false;
    for (const archivo of archivos) {
      try {
        const nuevo = await subirAsset(token, proyectoId, archivo);
        if (nuevo.analysis?.tipo === "logo") huboLogo = true;
      } catch (err) {
        fallos.push(
          `${archivo.name}: ${mensajeDeError(err, "no se pudo subir")}`,
        );
      }
    }
    setErroresSubida(fallos);
    try {
      const datos = await listarAssets(token, proyectoId);
      setAssets(datos);
      setErrorCarga(null);
      onCambio(datos, huboLogo);
    } catch (err) {
      setErrorCarga(mensajeDeError(err, "No se pudieron recargar los assets"));
    } finally {
      setSubiendo(false);
    }
  };

  const alSeleccionarArchivos = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const archivos = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    void subirArchivos(archivos);
  };

  const alSoltar = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setArrastrando(false);
    if (subiendo) return;
    const archivos = Array.from(e.dataTransfer.files);
    void subirArchivos(archivos);
  };

  const borrar = async (asset: Asset): Promise<void> => {
    const etiqueta = asset.analysis
      ? ETIQUETA_TIPO[asset.analysis.tipo]
      : "asset";
    const ok = window.confirm(`¿Eliminar este ${etiqueta.toLowerCase()}?`);
    if (!ok) return;
    setBorrandoId(asset.id);
    setErrorBorrado(null);
    try {
      await borrarAsset(token, proyectoId, asset.id);
      const datos = await listarAssets(token, proyectoId);
      setAssets(datos);
      onCambio(datos, asset.analysis?.tipo === "logo");
    } catch (err) {
      setErrorBorrado(mensajeDeError(err, "No se pudo eliminar el asset"));
    } finally {
      setBorrandoId(null);
    }
  };

  const quitarFondo = async (asset: Asset): Promise<void> => {
    setQuitandoFondoId(asset.id);
    setErroresSinFondo((prev) => {
      const { [asset.id]: _quitado, ...resto } = prev;
      return resto;
    });
    try {
      const resultado = await quitarFondoAsset(token, proyectoId, asset.id);
      setResultadosSinFondo((prev) => ({ ...prev, [asset.id]: resultado }));
    } catch (err) {
      setErroresSinFondo((prev) => ({
        ...prev,
        [asset.id]: mensajeDeError(err, "No se pudo quitar el fondo"),
      }));
    } finally {
      setQuitandoFondoId(null);
    }
  };

  return (
    <section className={estilos.tarjeta} aria-labelledby="titulo-assets">
      <header className={estilos.encabezado}>
        <h3 id="titulo-assets">Assets del proyecto</h3>
        <p className={estilos.subtitulo}>
          Sube el logo y fotos de producto. Cada imagen se clasifica
          automaticamente (logo, foto de producto, textura de fondo).
        </p>
      </header>

      <div
        className={`${estilos.dropzone} ${arrastrando ? estilos.dropzoneActiva : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!subiendo) setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={alSoltar}
      >
        <label htmlFor="input-subir-asset" className={estilos.dropzoneLabel}>
          <input
            id="input-subir-asset"
            type="file"
            accept={ACEPTA_ARCHIVOS}
            multiple
            className={estilos.inputOculto}
            onChange={alSeleccionarArchivos}
            disabled={subiendo}
          />
          <span>
            {subiendo
              ? "Subiendo..."
              : "Arrastra imagenes aqui o haz clic para elegirlas"}
          </span>
          <span className={estilos.dropzoneNota}>
            PNG, JPG o WEBP · maximo 10 MB por archivo
          </span>
        </label>
      </div>

      {erroresSubida.length > 0 && (
        <ul className={estilos.listaErrores} role="alert">
          {erroresSubida.map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
      )}

      {errorBorrado && (
        <p className={estilos.mensajeError} role="alert">
          {errorBorrado}
        </p>
      )}

      {cargando && <p className={estilos.mensaje}>Cargando assets...</p>}
      {errorCarga && !cargando && (
        <p className={estilos.mensajeError} role="alert">
          {errorCarga}
        </p>
      )}
      {!cargando && !errorCarga && assets.length === 0 && (
        <p className={estilos.mensaje}>Todavia no hay assets subidos.</p>
      )}

      {assets.length > 0 && (
        <ul className={estilos.grillaAssets} aria-label="Assets subidos">
          {assets.map((asset) => {
            const tipo = asset.analysis?.tipo ?? null;
            const resultado = resultadosSinFondo[asset.id];
            const errorFondo = erroresSinFondo[asset.id];
            return (
              <li key={asset.id} className={estilos.tarjetaAsset}>
                <div className={estilos.miniaturaWrap}>
                  <img
                    src={asset.url}
                    alt={asset.analysis?.descripcion ?? "Asset subido"}
                    className={estilos.miniatura}
                  />
                </div>

                <div className={estilos.infoAsset}>
                  <span
                    className={`${estilos.badgeTipo} ${
                      tipo ? estilos[`tipo_${tipo}`] : estilos.tipoDesconocido
                    }`}
                  >
                    {tipo ? ETIQUETA_TIPO[tipo] : "Sin clasificar"}
                  </span>
                  {asset.analysis?.descripcion && (
                    <p className={estilos.descripcionAsset}>
                      {asset.analysis.descripcion}
                    </p>
                  )}
                  {!asset.analysis && (
                    <p className={estilos.notaDegradada}>
                      La clasificacion automatica no estuvo disponible para este
                      archivo.
                    </p>
                  )}
                  <p className={estilos.metaAsset}>
                    {formatearTamano(asset.size_bytes)}
                    {asset.width && asset.height
                      ? ` · ${asset.width}×${asset.height}px`
                      : ""}
                  </p>
                </div>

                <div className={estilos.accionesAsset}>
                  {tipo === "foto_producto" && (
                    <button
                      type="button"
                      className="boton boton-secundario"
                      onClick={() => void quitarFondo(asset)}
                      disabled={quitandoFondoId === asset.id}
                    >
                      {quitandoFondoId === asset.id
                        ? "Quitando fondo..."
                        : "Quitar fondo"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="boton boton-peligro"
                    onClick={() => void borrar(asset)}
                    disabled={borrandoId === asset.id}
                  >
                    {borrandoId === asset.id ? "Eliminando..." : "Eliminar"}
                  </button>
                </div>

                {errorFondo && (
                  <p className={estilos.mensajeError} role="alert">
                    {errorFondo}
                  </p>
                )}

                {resultado && (
                  <div className={estilos.resultadoSinFondo}>
                    <p className={estilos.resultadoTitulo}>Fondo eliminado</p>
                    <div className={estilos.miniaturaWrap}>
                      <img
                        src={resultado.url}
                        alt="Resultado sin fondo (fondo a cuadros = transparencia)"
                        className={estilos.miniatura}
                      />
                    </div>
                    <a
                      className="boton boton-secundario"
                      href={resultado.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir imagen sin fondo
                    </a>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
